/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the errors, and displays a fallback UI.
 */

import React from 'react';
import logger from '../utils/logger';
import { getAppInsights } from '../config/telemetry';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    logger.error('React Error Boundary caught error', {
      errorMessage: error.toString(),
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    // Report to Application Insights
    const ai = getAppInsights();
    if (ai) {
      ai.trackException({ exception: error, properties: { componentStack: errorInfo.componentStack } });
    }

    this.setState({
      error,
      errorInfo
    });
  }

  handleDownloadLogs = () => {
    logger.downloadLogs();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '40px',
          maxWidth: '800px',
          margin: '0 auto',
          fontFamily: 'Arial, sans-serif'
        }}>
          <h1 style={{ color: '#dc3545' }}>⚠️ Something went wrong</h1>
          <p>The application encountered an unexpected error. This has been logged for debugging.</p>
          
          <div style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '15px',
            marginTop: '20px'
          }}>
            <h3>Error Details:</h3>
            <p style={{ fontFamily: 'monospace', fontSize: '14px' }}>
              {this.state.error && this.state.error.toString()}
            </p>
          </div>

          <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
            <button 
              onClick={this.handleReload}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007cba',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Reload Page
            </button>
            
            <button 
              onClick={this.handleDownloadLogs}
              style={{
                padding: '10px 20px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Download Error Logs
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <details style={{ marginTop: '20px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Show Component Stack
              </summary>
              <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px',
                marginTop: '10px'
              }}>
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
